import find from 'lodash-es/find'
import React from 'react'
import { StringParam, useQueryParam } from 'use-query-params'

import {
  ConcealedText,
  ContributionSideSummaryHeader,
  SideSummary,
  useDashboardPermissions,
} from '@components'
import { ButtonClean, useModalContext } from '@fourthwall/components'
import { getCountryNameByCode } from '@fourthwall/utils/dist/inputs'

import * as S from '../../Order.styled'
import type { Order } from '../../Order.types'
import { OrderEmailModal } from '../../modules/OrderEmailModal'
import { OrderShippingAddressModal } from '../../modules/OrderShippingAddressModal'
import { SupporterPhoneModal } from '../../modules/SupporterPhoneModal'
import { getFullName } from '../../utils/getFullName'

type OrderSideSummaryProps = {
  order?: Order
}

export const OrderSideSummary = ({ order }: OrderSideSummaryProps) => {
  const { open } = useModalContext()
  const { hasPermission } = useDashboardPermissions()
  const [isAddressModalOpen, setIsAddressModalOpen] = useQueryParam('editAddress', StringParam)
  const fullName = getFullName(
    order?.shipping.address?.firstName,
    order?.shipping.address?.lastName,
  )

  const renderSupporterContact = () => {
    if (order?.salesChannel?.type === 'TikTok') {
      return (
        <SideSummary.Item title="Supporter contact">
          <S.Email>N/A (Purchased from TikTok)</S.Email>
        </SideSummary.Item>
      )
    }

    return (
      <SideSummary.Item title="Supporter contact">
        <S.Email title={order?.email}>{order?.email}</S.Email>
        {hasPermission('contributions.orders.edit') && order?.email && (
          <ButtonClean
            label="Edit"
            onClick={() => {
              open(OrderEmailModal, { orderId: order.id, initialValues: { email: order.email } })
            }}
          />
        )}

        {order?.supporterPhoneNumber && (
          <>
            <S.Phone title={order?.supporterPhoneNumber}>{order?.supporterPhoneNumber}</S.Phone>
            {hasPermission('contributions.orders.edit') && (
              <ButtonClean
                label="Edit"
                onClick={() => {
                  open(SupporterPhoneModal, {
                    orderId: order.id,
                    initialValues: { phoneNumber: order.supporterPhoneNumber || '' },
                  })
                }}
              />
            )}
          </>
        )}
      </SideSummary.Item>
    )
  }

  const renderAdditionalField = (name: string, label?: string) => {
    const additionalField = find(order?.additionalFields, { name })

    if (additionalField) {
      return <SideSummary.Item title={label || name}>{additionalField.value}</SideSummary.Item>
    }

    return null
  }

  return (
    <SideSummary>
      {isAddressModalOpen && <OrderShippingAddressModal close={() => setIsAddressModalOpen('')} />}

      {fullName && (
        <SideSummary.Item>
          <ContributionSideSummaryHeader name={fullName} />
        </SideSummary.Item>
      )}
      {order?.shipping.address && (
        <SideSummary.Item title="Shipping address">
          <ConcealedText showLabel="View shipping address" hideLabel="Hide shipping address">
            {order?.shipping.address && (
              <>
                <p>{fullName}</p>
                {order?.shipping.address.phone && <p>{order?.shipping.address.phone}</p>}
                <p>{order?.shipping.address.address1}</p>
                <p>{order?.shipping.address.address2}</p>
                <p>{`${order?.shipping.address.city} ${order?.shipping.address.state}${order?.shipping.address.zip ? ` ${order?.shipping.address.zip}` : ''}`}</p>
                <p>{getCountryNameByCode(order?.shipping.address.country)}</p>
              </>
            )}
          </ConcealedText>
          {order?.status !== 'COMPLETED' &&
            order?.status !== 'CANCELLED' &&
            hasPermission('contributions.orders.edit') && (
              <ButtonClean
                label="Edit shipping address"
                onClick={() => setIsAddressModalOpen('true')}
              />
            )}
        </SideSummary.Item>
      )}
      {renderSupporterContact()}
      {renderAdditionalField('employer')}
      {renderAdditionalField('occupation')}
      {renderAdditionalField('username')}
      {renderAdditionalField('messageSelectedOption', 'Message choice')}
    </SideSummary>
  )
}
